import React, { useEffect, useState, Suspense, lazy, createElement } from 'react';
// perf-optimized
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { CartProvider } from './lib/cart';
import { TableOrderProvider } from './lib/table-context';
import { KitchenPauseProvider } from './lib/kitchen-pause-context';
import { LanguageProvider } from './lib/i18n';
import { KitchenPausedBanner } from './components/restaurant/KitchenPausedBanner';
import { getSubdomainSlug } from './lib/subdomain';
import { ScrollToTop } from './components/ui/ScrollToTop';
// Eager Customer Pages (Critical Path)
import { HomePage } from './pages/HomePage';
import { RestaurantPage } from './pages/RestaurantPage';
import { PublicMenuPage } from './pages/PublicMenuPage';
import { CartPage } from './pages/CartPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { RequireAdminAuth } from './components/admin/RequireAdminAuth';
import { RequirePlatformAdmin } from './components/admin/RequirePlatformAdmin';
// Lazy Customer Pages
const RestaurantListingPage = lazy(() => import('./pages/RestaurantListingPage').then(m => ({
  default: m.RestaurantListingPage
})));
const MenuPage = lazy(() => import('./pages/MenuPage').then(m => ({
  default: m.MenuPage
})));
const OrderConfirmationPage = lazy(() => import('./pages/OrderConfirmationPage').then(m => ({
  default: m.OrderConfirmationPage
})));
const TrackingPage = lazy(() => import('./pages/TrackingPage').then(m => ({
  default: m.TrackingPage
})));
const TablePage = lazy(() => import('./pages/TablePage').then(m => ({
  default: m.TablePage
})));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage').then(m => ({
  default: m.OnboardingPage
})));
const TermsRestaurantsPage = lazy(() => import('./pages/TermsRestaurantsPage').then(m => ({
  default: m.TermsRestaurantsPage
})));
const NatuCafePage = lazy(() => import('./pages/NatuCafePage').then(m => ({
  default: m.NatuCafePage
})));
const InvoicePage = lazy(() => import('./pages/InvoicePage').then(m => ({
  default: m.InvoicePage
})));
const TicketPage = lazy(() => import('./pages/TicketPage').then(m => ({
  default: m.TicketPage
})));
// Lazy Admin Pages
const LoginPage = lazy(() => import('./pages/admin/LoginPage').then(m => ({
  default: m.LoginPage
})));
const NoizaMetricsPage = lazy(() => import('./pages/admin/NoizaMetricsPage').then(m => ({
  default: m.NoizaMetricsPage
})));
const DashboardPage = lazy(() => import('./pages/admin/DashboardPage').then(m => ({
  default: m.DashboardPage
})));
const KDSPage = lazy(() => import('./pages/admin/KDSPage').then(m => ({
  default: m.KDSPage
})));
const InventoryPage = lazy(() => import('./pages/admin/InventoryPage').then(m => ({
  default: m.InventoryPage
})));
const MetricsPage = lazy(() => import('./pages/admin/MetricsPage').then(m => ({
  default: m.MetricsPage
})));
const MenuManagementPage = lazy(() => import('./pages/admin/MenuManagementPage').then(m => ({
  default: m.MenuManagementPage
})));
const ImportPage = lazy(() => import('./pages/admin/ImportPage').then(m => ({
  default: m.ImportPage
})));
const WaiterPOSPage = lazy(() => import('./pages/admin/WaiterPOSPage').then(m => ({
  default: m.WaiterPOSPage
})));
const StationSelectPage = lazy(() => import('./pages/admin/StationSelectPage').then(m => ({
  default: m.StationSelectPage
})));
const CajaOverviewPage = lazy(() => import('./pages/admin/CajaOverviewPage').then(m => ({
  default: m.CajaOverviewPage
})));
const POSCajaPage = lazy(() => import('./pages/admin/POSCajaPage').then(m => ({
  default: m.POSCajaPage
})));
const CancelledOrdersPage = lazy(() => import('./pages/admin/CancelledOrdersPage').then(m => ({
  default: m.CancelledOrdersPage
})));
const TableManagementPage = lazy(() => import('./pages/admin/TableManagementPage').then(m => ({
  default: m.TableManagementPage
})));
const TableQRPage = lazy(() => import('./pages/admin/TableQRPage').then(m => ({
  default: m.TableQRPage
})));
const MenuQRPage = lazy(() => import('./pages/admin/MenuQRPage').then(m => ({
  default: m.MenuQRPage
})));
const LoyaltyDashboardPage = lazy(() => import('./pages/admin/LoyaltyDashboardPage').then(m => ({
  default: m.LoyaltyDashboardPage
})));
const LoyaltyRedemptionPage = lazy(() => import('./pages/admin/LoyaltyRedemptionPage').then(m => ({
  default: m.LoyaltyRedemptionPage
})));
const WebOrdersPage = lazy(() => import('./pages/admin/WebOrdersPage').then(m => ({
  default: m.WebOrdersPage
})));
const ServicioExpressPage = lazy(() => import('./pages/admin/ServicioExpressPage').then(m => ({
  default: m.ServicioExpressPage
})));
const InvoiceRequestsPage = lazy(() => import('./pages/admin/InvoiceRequestsPage').then(m => ({
  default: m.InvoiceRequestsPage
})));
const CreateRestaurantPage = lazy(() => import('./pages/admin/CreateRestaurantPage').then(m => ({
  default: m.CreateRestaurantPage
})));
const DriversListPage = lazy(() => import('./pages/admin/DriversListPage').then(m => ({
  default: m.DriversListPage
})));
const CreateDriverPage = lazy(() => import('./pages/admin/CreateDriverPage').then(m => ({
  default: m.CreateDriverPage
})));
const VehiclesListPage = lazy(() => import('./pages/admin/VehiclesListPage').then(m => ({
  default: m.VehiclesListPage
})));
const CreateVehiclePage = lazy(() => import('./pages/admin/CreateVehiclePage').then(m => ({
  default: m.CreateVehiclePage
})));
const AssignmentsPage = lazy(() => import('./pages/admin/AssignmentsPage').then(m => ({
  default: m.AssignmentsPage
})));
const UsersAdminPage = lazy(() => import('./pages/admin/UsersAdminPage').then(m => ({
  default: m.UsersAdminPage
})));
const EnrollmentPage = lazy(() => import('./pages/admin/EnrollmentPage').then(m => ({
  default: m.EnrollmentPage
})));
const EnrollmentRestaurantPage = lazy(() => import('./pages/admin/EnrollmentRestaurantPage').then(m => ({
  default: m.EnrollmentRestaurantPage
})));
const EnrollmentMenuUpdatePage = lazy(() => import('./pages/admin/EnrollmentMenuUpdatePage').then(m => ({
  default: m.EnrollmentMenuUpdatePage
})));
const EnrollmentPlatformAdminPage = lazy(() => import('./pages/admin/EnrollmentPlatformAdminPage').then(m => ({
  default: m.EnrollmentPlatformAdminPage
})));
const EnrollmentRestaurantAdminPage = lazy(() => import('./pages/admin/EnrollmentRestaurantAdminPage').then(m => ({
  default: m.EnrollmentRestaurantAdminPage
})));
const EnrollmentMenuImagesPage = lazy(() => import('./pages/admin/EnrollmentMenuImagesPage').then(m => ({
  default: m.EnrollmentMenuImagesPage
})));
const ReceiptGeneratorPage = lazy(() => import('./pages/admin/ReceiptGeneratorPage').then(m => ({
  default: m.ReceiptGeneratorPage
})));
const GlobalAdminLoginPage = lazy(() => import('./pages/admin/GlobalAdminLoginPage').then(m => ({
  default: m.GlobalAdminLoginPage
})));
// Lazy Driver Pages
const DriverLoginPage = lazy(() => import('./pages/driver/DriverLoginPage').then(m => ({
  default: m.DriverLoginPage
})));
const DriverAppPage = lazy(() => import('./pages/driver/DriverAppPage').then(m => ({
  default: m.DriverAppPage
})));
// Tenant Access
import { TenantProvider } from './lib/tenant-context';
// ─── MAINTENANCE GATE ───
// Slugs under maintenance — only accessible with ?dev=noiza2026
const MAINTENANCE_SLUGS = ['noiza.restaurant'];
function MaintenancePage() {
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const handleUnlock = () => {
    if (code === 'noiza2026') {
      sessionStorage.setItem('noiza_dev_access', 'true');
      window.location.reload();
    } else {
      setError(true);
    }
  };
  return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center p-6" data-id="element-0" data-mp="2vwk5edsex22u">
      <div className="max-w-md w-full text-center" data-id="element-1" data-mp="2qx7jbv9d7z1b">
        <div className="w-16 h-16 mx-auto mb-6 bg-[#141414] rounded-2xl flex items-center justify-center border border-white/10" data-id="element-2" data-mp="2qvweds46ol8m">
          <span className="text-2xl" data-id="element-3" data-mp="2m07c3h0s4q8m">🔧</span>
        </div>
        <h1 className="text-2xl font-black text-white mb-2" data-id="element-4" data-mp="2hxkqhxupc1jv">
          En Mantenimiento
        </h1>
        <p className="text-white/40 text-sm mb-8" data-id="element-5" data-mp="2d4rkz7resp6q">
          Este restaurante está temporalmente en modo de pruebas. Volveremos
          pronto.
        </p>
        <div className="space-y-3" data-id="element-6" data-mp="237n1xgsv19u5">
          <input type="password" placeholder="Código de acceso" value={code} onChange={e => {
          setCode(e.target.value);
          setError(false);
        }} onKeyDown={e => e.key === 'Enter' && handleUnlock()} className="w-full px-4 py-3 rounded-xl bg-[#141414] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#006847] focus:border-transparent text-center" data-id="element-7" data-mp="238o9vcekxg7n" />
          {error && <p className="text-[#CE1126] text-xs" data-id="element-8" data-mp="iqdrvn4tlw7j">Código incorrecto</p>}
          <button onClick={handleUnlock} className="w-full py-3 rounded-xl bg-[#006847] text-white font-bold text-sm hover:bg-[#005a3d] transition-colors" data-id="element-9" data-mp="nnmbls5ma8aw">
            Acceder
          </button>
        </div>
      </div>
    </div>;
}
/** Routes Capricho de Canela to PublicMenuPage (premium theme), others to RestaurantPage */
function SmartRestaurantPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  // Maintenance gate: check URL param or session
  if (slug && MAINTENANCE_SLUGS.includes(slug)) {
    const urlParams = new URLSearchParams(window.location.search);
    const hasDevParam = urlParams.get('dev') === 'noiza2026';
    const hasSessionAccess = sessionStorage.getItem('noiza_dev_access') === 'true';
    if (hasDevParam && !hasSessionAccess) {
      sessionStorage.setItem('noiza_dev_access', 'true');
    }
    if (!hasDevParam && !hasSessionAccess) {
      return <MaintenancePage data-id="element-10" data-mp="2p62t3opuh8qa" />;
    }
  }
  if (slug === 'capricho-de-canela') {
    return <PublicMenuPage data-id="element-11" data-mp="2u330c2yfkhiz" />;
  }
  return <RestaurantPage data-id="element-12" data-mp="2u2behjumxy5w" />;
}
/** Redirects /admin/:slug → /admin/:slug/operacion (station select) */
function AdminRedirect() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  return <Navigate to={`/admin/${slug}/operacion`} replace data-id="element-13" data-mp="21g8f33tshe4g" />;
}
/**
 * Subdomain-aware admin redirect.
 * When on a tenant subdomain, /admin → /admin/dashboard
 */
function SubdomainAdminRedirect({
  slug
}: {
  slug: string;
}) {
  return <Navigate to="/admin/operacion" replace data-id="element-14" data-mp="2b7cxkdiobh41" />;
}
/**
 * Wraps TenantProvider to inject the subdomain slug
 * when there's no :slug in the path params.
 */
function SubdomainTenantProvider({
  children,
  slug
}: {
  children: React.ReactNode;
  slug: string;
}) {
  // We pass the slug through a context-compatible wrapper
  // TenantProvider reads from useParams, but we need to override it.
  // Instead, we'll use the updated TenantProvider that accepts an optional slug prop.
  return <TenantProvider subdomainSlug={slug} data-id="element-15" data-mp="3mwzj1qb2i36d">{children}</TenantProvider>;
}
/**
 * Wrapper for marketplace admin routes that need TenantProvider.
 * TenantProvider reads :slug from useParams and handles auth + access.
 */
function TenantAdminRoute({
  children
}: {
  children: React.ReactNode;
}) {
  return <TenantProvider data-id="element-16" data-mp="388mtbb2kpgkb">{children}</TenantProvider>;
}
/**
 * Main routing component.
 * Detects subdomain and renders appropriate routes.
 */
function AppRoutes() {
  const subdomainSlug = getSubdomainSlug();
  // ─── SUBDOMAIN MODE ───
  // When accessed via {slug}.noiza.estrategiasdigitalesaz.com
  if (subdomainSlug) {
    return <SubdomainTenantProvider slug={subdomainSlug} data-id="element-17" data-mp="2pb22vzk5u9xl">
        <Suspense fallback={<div className="min-h-screen bg-[#FBF7F2] flex items-center justify-center" data-id="element-19" data-mp="2fng08uvqiwo3">
              <div className="w-8 h-8 border-4 border-[#8B5E3C] border-t-transparent rounded-full animate-spin" data-id="element-20" data-mp="2fpsqrwfk6fcr"></div>
            </div>} data-id="element-18" data-mp="2pbln5mxa9aes">
          <Routes data-id="element-21" data-mp="26o31ox95fsi5">
            {/* Customer Routes */}
            <Route path="/" element={<SmartRestaurantPage data-id="element-23" data-mp="2anlaiunp4d4m" />} data-id="element-22" data-mp="2aod8gc4rmmy2" />
            <Route path="/menu" element={<MenuPage data-id="element-25" data-mp="2ydxr0tx0bi6t" />} data-id="element-24" data-mp="2aomyu0tq2w4a" />
            <Route path="/cart" element={<CartPage data-id="element-27" data-mp="2yf8wqza0j00k" />} data-id="element-26" data-mp="2ydnz30sej0yh" />
            <Route path="/checkout" element={<CheckoutPage data-id="element-29" data-mp="33dk3h150guek" />} data-id="element-28" data-mp="2yg0nnwso09ki" />
            <Route path="/order-confirmation" element={<OrderConfirmationPage data-id="element-31" data-mp="2pm5mezunz3a5" />} data-id="element-30" data-mp="33bpixnxnj4b0" />
            <Route path="/tracking/:orderId" element={<TrackingPage data-id="element-33" data-mp="2tmpgfujxxqs2" />} data-id="element-32" data-mp="2pldqfnx5n3jc" />
            <Route path="/t/:tableId" element={<TablePage data-id="element-35" data-mp="2tl4omcp0glxw" />} data-id="element-34" data-mp="2tlwh3enba3jy" />
            {/* QR code routes — match QR URL format from TableQRPage */}
            <Route path="/mesa/:tableNumber/:token" element={<TablePage data-id="element-37" data-mp="3m4jyaiz6syfr" />} data-id="element-36" data-mp="3hayxyka4fsde" />
            <Route path="/mesa/:tableNumber" element={<TablePage data-id="element-39" data-mp="1ao9qgzek4nnu" />} data-id="element-38" data-mp="3m4tddbip1l6o" />

            {/* Admin Routes */}
            <Route path="/admin" element={<SubdomainAdminRedirect slug={subdomainSlug} data-id="element-41" data-mp="15q85ua1hgga9" />} data-id="element-40" data-mp="1amq4qta4tkeo" />
            <Route path="/admin/login" element={<LoginPage data-id="element-43" data-mp="1kdb6gpl3r51t" />} data-id="element-42" data-mp="1ke30vx2yqwqi" />
            <Route path="/admin/operacion" element={<RequireAdminAuth data-id="element-45" data-mp="1ffrrfngkyo8t">
                  <StationSelectPage data-id="element-46" data-mp="rpp0xbsbdqcb" />
                </RequireAdminAuth>} data-id="element-44" data-mp="1kdky0hlspk9m" />
            <Route path="/admin/dashboard" element={<RequireAdminAuth data-id="element-48" data-mp="msowt6f0yxnu">
                  <DashboardPage data-id="element-49" data-mp="11oe8obqhxueu" />
                </RequireAdminAuth>} data-id="element-47" data-mp="mqlupo0lle2r" />
            <Route path="/admin/kds" element={<RequireAdminAuth data-id="element-51" data-mp="wr4j8w33xir0">
                  <KDSPage data-id="element-52" data-mp="2d28kuhm70hpa" />
                </RequireAdminAuth>} data-id="element-50" data-mp="wo8ofgmk3a6s" />
            <Route path="/admin/caja" element={<RequireAdminAuth data-id="element-54" data-mp="hmth3kdihget">
                  <CajaOverviewPage data-id="element-55" data-mp="hnv14eci9d8o" />
                </RequireAdminAuth>} data-id="element-53" data-mp="284gjkwznrj8f" />
            <Route path="/admin/pos-caja" element={<RequireAdminAuth data-id="element-57" data-mp="qmzxbe0lguho">
                  <POSCajaPage data-id="element-58" data-mp="qlye2m9fd1ov" />
                </RequireAdminAuth>} data-id="element-56" data-mp="mmxv93qy5z1v" />
            <Route path="/admin/cancelled" element={<RequireAdminAuth data-id="element-60" data-mp="10kmk0mo4xqc7">
                  <CancelledOrdersPage data-id="element-61" data-mp="10jl70cnk6tru" />
                </RequireAdminAuth>} data-id="element-59" data-mp="vo5upp5795xj" />
            <Route path="/admin/inventory" element={<RequireAdminAuth data-id="element-63" data-mp="1acafqzrq5tjh">
                  <InventoryPage data-id="element-64" data-mp="1ed2n1nh1dod4" />
                </RequireAdminAuth>} data-id="element-62" data-mp="15inyeuam91hc" />
            <Route path="/admin/metrics" element={<RequireAdminAuth data-id="element-66" data-mp="1ja2mhfjdrsv9">
                  <MetricsPage data-id="element-67" data-mp="1oaqmurti1m25" />
                </RequireAdminAuth>} data-id="element-65" data-mp="1jc6zcmz9c27o" />
            <Route path="/admin/menu" element={<RequireAdminAuth data-id="element-69" data-mp="1ycubsij8tigr">
                  <MenuManagementPage data-id="element-70" data-mp="1tfubypczlby3" />
                </RequireAdminAuth>} data-id="element-68" data-mp="1yex5pdninvqr" />
            <Route path="/admin/import" element={<RequireAdminAuth data-id="element-72" data-mp="1kfxjsdre1uya">
                  <ImportPage data-id="element-73" data-mp="1kgqrpi232qi0" />
                </RequireAdminAuth>} data-id="element-71" data-mp="1og95ltvhm1bc" />
            <Route path="/admin/waiter" element={<RequireAdminAuth data-id="element-75" data-mp="2c6bqnq0zi70g">
                  <WaiterPOSPage data-id="element-76" data-mp="2c7dg4ziqyw1p" />
                </RequireAdminAuth>} data-id="element-74" data-mp="2h4daid68iacz" />
            <Route path="/admin/tables" element={<RequireAdminAuth data-id="element-78" data-mp="23d8fjodta68z">
                  <TableManagementPage data-id="element-79" data-mp="23c75nn8j7psu" />
                </RequireAdminAuth>} data-id="element-77" data-mp="27eby4in8ugdw" />
            <Route path="/admin/qr" element={<RequireAdminAuth data-id="element-81" data-mp="2v3wklbilvvrx">
                  <TableQRPage data-id="element-82" data-mp="t7xc8m4vjxom" />
                </RequireAdminAuth>} data-id="element-80" data-mp="2zyae18dpjsty" />
            <Route path="/admin/menu-qr" element={<RequireAdminAuth data-id="element-84" data-mp="y937aqm0bx1b">
                  <MenuQRPage data-id="element-85" data-mp="jidwvqgdelyi" />
                </RequireAdminAuth>} data-id="element-83" data-mp="ta9vr3qa6g3s" />
            <Route path="/admin/loyalty" element={<RequireAdminAuth data-id="element-87" data-mp="oeuiacw6qm7w">
                  <LoyaltyDashboardPage data-id="element-88" data-mp="ah67jxvu0vgk" />
                </RequireAdminAuth>} data-id="element-86" data-mp="ogxh9mfbfpor" />
            <Route path="/admin/loyalty/redemption" element={<RequireAdminAuth data-id="element-90" data-mp="k1jfsifepx4">
                  <LoyaltyRedemptionPage data-id="element-91" data-mp="kt78h5s7jcu" />
                </RequireAdminAuth>} data-id="element-89" data-mp="fgsk053u67ob" />
            <Route path="/admin/invoice-requests" element={<RequireAdminAuth data-id="element-93" data-mp="1uxsosftq2nmr">
                  <InvoiceRequestsPage data-id="element-94" data-mp="1zwdqkjomhfdb" />
                </RequireAdminAuth>} data-id="element-92" data-mp="5il8i1sbghtp" />

            {/* Platform Admin Routes (Subdomain) */}
            <Route path="/admin/enrollment/receipts" element={<RequirePlatformAdmin data-id="element-96" data-mp="3fgmpho0yh01y">
                  <ReceiptGeneratorPage data-id="element-97" data-mp="3fdh2cebw088v" />
                </RequirePlatformAdmin>} data-id="element-95" data-mp="3key1a4at9kmt" />

            {/* Catch-all */}
            <Route path="/factura/:paymentId" element={<InvoicePage data-id="element-99" data-mp="3pc6ia1k83imh" />} data-id="element-98" data-mp="3pd7vabksuf6u" />
            <Route path="/ticket/:orderNumber" element={<TicketPage data-id="element-101" data-mp="6y727brk5d3q" />} data-id="element-100" data-mp="6z8flmw1qbom" />
            <Route path="*" element={<Navigate to="/" replace data-id="element-103" data-mp="2y6qhyvufg0d" />} data-id="element-102" data-mp="71l5cm81pqc8" />
          </Routes>
        </Suspense>
      </SubdomainTenantProvider>;
  }
  // ─── MARKETPLACE MODE ───
  // When accessed via noizacr.com
  return <Suspense fallback={<div className="min-h-screen bg-[#FBF7F2] flex items-center justify-center" data-id="element-105" data-mp="bzmplah7jvpa">
          <div className="w-8 h-8 border-4 border-[#8B5E3C] border-t-transparent rounded-full animate-spin" data-id="element-106" data-mp="2ioj6essei4a4"></div>
        </div>} data-id="element-104" data-mp="bylcl0gmsz4x">
      <Routes data-id="element-107" data-mp="2drj0qizgr3jj">
        {/* Marketplace Routes */}
        <Route path="/" element={<HomePage data-id="element-109" data-mp="1rvr6u3agqlz8" />} data-id="element-108" data-mp="1rx2be5bqfxre" />
        <Route path="/restaurants" element={<RestaurantListingPage data-id="element-111" data-mp="1wvvlrovq38nc" />} data-id="element-110" data-mp="1rw97rd82lodh" />
        <Route path="/terminos-restaurantes" element={<TermsRestaurantsPage data-id="element-113" data-mp="21mlvtff3fzod" />} data-id="element-112" data-mp="1wutzspd651qu" />
        <Route path="/natucafe" element={<NatuCafePage data-id="element-115" data-mp="21ooqicr6ygzf" />} data-id="element-114" data-mp="21nwu4zprsf83" />
        <Route path="/onboarding" element={<OnboardingPage data-id="element-117" data-mp="26kdjfx8pgzmr" />} data-id="element-116" data-mp="26lf6l02zxcku" />

        {/* Shared customer routes (work without slug for legacy/direct links) */}
        <Route path="/cart" element={<CartPage data-id="element-119" data-mp="190aug2jnxuqi" />} data-id="element-118" data-mp="18zhjepdo8j2k" />
        <Route path="/checkout" element={<CheckoutPage data-id="element-121" data-mp="1dxss2ws8cnj7" />} data-id="element-120" data-mp="1dwhp2z6lzjt5" />
        <Route path="/order-confirmation" element={<OrderConfirmationPage data-id="element-123" data-mp="1ivkuikqi3s1n" />} data-id="element-122" data-mp="1dz3wmyti1zbd" />

        {/* Restaurant Specific Routes */}
        <Route path="/r/:slug" element={<SmartRestaurantPage data-id="element-125" data-mp="1nvgxqhrdjfg5" />} data-id="element-124" data-mp="1nv79aylz9gck" />
        <Route path="/r/:slug/menu" element={<MenuPage data-id="element-127" data-mp="2ubj9t6c9vabb" />} data-id="element-126" data-mp="1nwie91r5su59" />
        <Route path="/r/:slug/cart" element={<CartPage data-id="element-129" data-mp="2uazt1sycyryv" />} data-id="element-128" data-mp="2uc2g1psk8a9i" />
        <Route path="/r/:slug/checkout" element={<CheckoutPage data-id="element-131" data-mp="2z9b1bz908uez" />} data-id="element-130" data-mp="2udm0ztp5v9hm" />
        <Route path="/r/:slug/order-confirmation" element={<OrderConfirmationPage data-id="element-133" data-mp="nlz50tcliqax" />} data-id="element-132" data-mp="2z8syukvr1jym" />
        <Route path="/r/:slug/tracking/:orderId" element={<TrackingPage data-id="element-135" data-mp="innvyku4kjtr" />} data-id="element-134" data-mp="np3eo68tye8z" />
        <Route path="/r/:slug/t/:tableId" element={<TablePage data-id="element-137" data-mp="dx7f0qr3ig2n" />} data-id="element-136" data-mp="iofqrtfwediz" />
        {/* QR code routes — match QR URL format from TableQRPage */}
        <Route path="/r/:slug/mesa/:tableNumber/:token" element={<TablePage data-id="element-139" data-mp="8w0ad0k91x07" />} data-id="element-138" data-mp="8y49z6b489uo" />
        <Route path="/r/:slug/mesa/:tableNumber" element={<TablePage data-id="element-141" data-mp="40kwi8m8s13s" />} data-id="element-140" data-mp="3yrrsyv6vqyf" />

        {/* Admin Routes */}
        <Route path="/admin/login" element={<GlobalAdminLoginPage data-id="element-143" data-mp="3w211j8ycjn7e" />} data-id="element-142" data-mp="3w2ariwje5ud3" />
        <Route path="/admin/metrics" element={<RequirePlatformAdmin data-id="element-145" data-mp="3r496w76bhmzi">
              <NoizaMetricsPage data-id="element-146" data-mp="3m4mjx60ossdi" />
            </RequirePlatformAdmin>} data-id="element-144" data-mp="3w56nwgflcazf" />
        <Route path="/admin/restaurants/new" element={<RequirePlatformAdmin data-id="element-148" data-mp="1pbujwtox1sc8">
              <CreateRestaurantPage data-id="element-149" data-mp="1kd9pxc09c4w8" />
            </RequirePlatformAdmin>} data-id="element-147" data-mp="3m6zau8ofad2p" />
        <Route path="/admin/drivers" element={<RequirePlatformAdmin data-id="element-151" data-mp="2c6sxgr85v9ts">
              <DriversListPage data-id="element-152" data-mp="2789b4ppnnw0w" />
            </RequirePlatformAdmin>} data-id="element-150" data-mp="1kbggjoy9f6kj" />
        <Route path="/admin/drivers/new" element={<RequirePlatformAdmin data-id="element-154" data-mp="22fyatlr0687t">
              <CreateDriverPage data-id="element-155" data-mp="1xig13t55voyr" />
            </RequirePlatformAdmin>} data-id="element-153" data-mp="27alt32vey6dy" />
        <Route path="/admin/vehicles" element={<RequirePlatformAdmin data-id="element-157" data-mp="1tifn29lz5fs8">
              <VehiclesListPage data-id="element-158" data-mp="1ohrmoxbuvmlc" />
            </RequirePlatformAdmin>} data-id="element-156" data-mp="1xglr39vfhh9g" />
        <Route path="/admin/vehicles/new" element={<RequirePlatformAdmin data-id="element-160" data-mp="1jj6w7pmeoha3">
              <CreateVehiclePage data-id="element-161" data-mp="1ejlnwolciwkp" />
            </RequirePlatformAdmin>} data-id="element-159" data-mp="1ok4f64f8pfcn" />
        <Route path="/admin/assignments" element={<RequirePlatformAdmin data-id="element-163" data-mp="3dw69vd7871eq">
              <AssignmentsPage data-id="element-164" data-mp="38xlhfzy7tm0u" />
            </RequirePlatformAdmin>} data-id="element-162" data-mp="1elydnnxcib8b" />
        <Route path="/admin/users" element={<RequirePlatformAdmin data-id="element-166" data-mp="3he411lz6th5m">
              <UsersAdminPage data-id="element-167" data-mp="3hcsuxfi9rxbc" />
            </RequirePlatformAdmin>} data-id="element-165" data-mp="38vsa0ifs2xrs" />
        <Route path="/admin/enrollment" element={<RequirePlatformAdmin data-id="element-169" data-mp="3q88l9mjtfuc6">
              <EnrollmentPage data-id="element-170" data-mp="3q99zu101iyyn" />
            </RequirePlatformAdmin>} data-id="element-168" data-mp="3le4ep4egpobm" />
        <Route path="/admin/enrollment/restaurant" element={<RequirePlatformAdmin data-id="element-172" data-mp="2yhcb4tp543sl">
              <EnrollmentRestaurantPage data-id="element-173" data-mp="32hczp75ydvdd" />
            </RequirePlatformAdmin>} data-id="element-171" data-mp="3v84r7rc2pvud" />
        <Route path="/admin/enrollment/menu-update" element={<RequirePlatformAdmin data-id="element-175" data-mp="37iiurbn35uq2">
              <EnrollmentMenuUpdatePage data-id="element-176" data-mp="3cg25w2ohlrds" />
            </RequirePlatformAdmin>} data-id="element-174" data-mp="32j64egx0a5iq" />
        <Route path="/admin/enrollment/platform-admin" element={<RequirePlatformAdmin data-id="element-178" data-mp="rpbfzl6rpj3d">
              <EnrollmentPlatformAdminPage data-id="element-179" data-mp="ro8rfjwx3sqm" />
            </RequirePlatformAdmin>} data-id="element-177" data-mp="mqgxkkcppwjs" />
        <Route path="/admin/enrollment/restaurant-admin" element={<RequirePlatformAdmin data-id="element-181" data-mp="3t88stig7ef1e">
              <EnrollmentRestaurantAdminPage data-id="element-182" data-mp="3t77glangbmi3" />
            </RequirePlatformAdmin>} data-id="element-180" data-mp="204q6lfftk8x" />
        <Route path="/admin/enrollment/menu-images" element={<RequirePlatformAdmin data-id="element-184" data-mp="71ka2a5vpkpq">
              <EnrollmentMenuImagesPage data-id="element-185" data-mp="6yemx0gt8swn" />
            </RequirePlatformAdmin>} data-id="element-183" data-mp="byki2qmagxjh" />
        <Route path="/admin/enrollment/receipts" element={<RequirePlatformAdmin data-id="element-187" data-mp="3aisr4rss8gje">
              <ReceiptGeneratorPage data-id="element-188" data-mp="3p5lvx34oe3xc" />
            </RequirePlatformAdmin>} data-id="element-186" data-mp="3fdpx1purm9s2" />
        <Route path="/admin/enrollment/metrics" element={<RequirePlatformAdmin data-id="element-190" data-mp="3kd2kabpjtiv6">
              <NoizaMetricsPage data-id="element-191" data-mp="2wp1f9crkwkki" />
            </RequirePlatformAdmin>} data-id="element-189" data-mp="3kf5i3hwy0gag" />

        <Route path="/admin/:slug" element={<AdminRedirect data-id="element-193" data-mp="2rpf4rb34xife" />} data-id="element-192" data-mp="2rqq5t3574c2t" />
        <Route path="/admin/:slug/login" element={<LoginPage data-id="element-195" data-mp="178pbuvwx8p5r" />} data-id="element-194" data-mp="2rq82xno12zlx" />
        <Route path="/admin/:slug/operacion" element={<TenantAdminRoute data-id="element-197" data-mp="1bznxmb0oifge">
              <StationSelectPage data-id="element-198" data-mp="1bzxnzzpmyomm" />
            </TenantAdminRoute>} data-id="element-196" data-mp="177ea11n1drha" />
        <Route path="/admin/:slug/dashboard" element={<TenantAdminRoute data-id="element-200" data-mp="128ta73bp59td">
              <DashboardPage data-id="element-201" data-mp="12a4d70xbidjf" />
            </TenantAdminRoute>} data-id="element-199" data-mp="xbayx6a7iii7" />
        <Route path="/admin/:slug/kds" element={<TenantAdminRoute data-id="element-203" data-mp="1uy04tr815f9c">
              <KDSPage data-id="element-204" data-mp="1uwyvpsakr2u9" />
            </TenantAdminRoute>} data-id="element-202" data-mp="1pynnflzxh0e5" />
        <Route path="/admin/:slug/caja" element={<TenantAdminRoute data-id="element-206" data-mp="1h0bpt00n92c7">
              <CajaOverviewPage data-id="element-207" data-mp="1kzazexxbln9z" />
            </TenantAdminRoute>} data-id="element-205" data-mp="1gyio7z4w18b2" />
        <Route path="/admin/:slug/pos-caja" element={<TenantAdminRoute data-id="element-209" data-mp="5l6jlm1b0lnu">
              <POSCajaPage data-id="element-210" data-mp="9kfdqs09aljx" />
            </TenantAdminRoute>} data-id="element-208" data-mp="5j3io6ym584c" />
        <Route path="/admin/:slug/cancelled" element={<TenantAdminRoute data-id="element-212" data-mp="pt4q3kljhgsl">
              <CancelledOrdersPage data-id="element-213" data-mp="l13d1wgjrm1i" />
            </TenantAdminRoute>} data-id="element-211" data-mp="9icd7e1h9a0y" />
        <Route path="/admin/:slug/inventory" element={<TenantAdminRoute data-id="element-215" data-mp="g29zk419fv1c">
              <InventoryPage data-id="element-216" data-mp="b2xi5yt5rg65" />
            </TenantAdminRoute>} data-id="element-214" data-mp="kza5mey40xsg" />
        <Route path="/admin/:slug/metrics" element={<TenantAdminRoute data-id="element-218" data-mp="18q5w98qtxmy4">
              <MetricsPage data-id="element-219" data-mp="18obhyda2ct30" />
            </TenantAdminRoute>} data-id="element-217" data-mp="b0uien27e8o8" />
        <Route path="/admin/:slug/menu" element={<TenantAdminRoute data-id="element-221" data-mp="yr6wkfo1l1n9">
              <MenuManagementPage data-id="element-222" data-mp="yuky5lovt6tn" />
            </TenantAdminRoute>} data-id="element-220" data-mp="13se1m6ysvmq8" />
        <Route path="/admin/:slug/import" element={<TenantAdminRoute data-id="element-224" data-mp="3jjs5u71dwtzn">
              <ImportPage data-id="element-225" data-mp="3jk1wzxy6176x" />
            </TenantAdminRoute>} data-id="element-223" data-mp="uqmztd7ds01n" />
        <Route path="/admin/:slug/waiter" element={<TenantAdminRoute data-id="element-227" data-mp="1uzw5igwbo3hy">
              <WaiterPOSPage data-id="element-228" data-mp="1v0o2nw5ki9ac" />
            </TenantAdminRoute>} data-id="element-226" data-mp="3fgcffsfwrpve" />
        <Route path="/admin/:slug/tables" element={<TenantAdminRoute data-id="element-230" data-mp="23z9hphbtz66d">
              <TableManagementPage data-id="element-231" data-mp="23yzrbsmvix05" />
            </TenantAdminRoute>} data-id="element-229" data-mp="1z1q2ae3ecncu" />
        <Route path="/admin/:slug/qr" element={<TenantAdminRoute data-id="element-233" data-mp="2dpumjmi3l6xq">
              <TableQRPage data-id="element-234" data-mp="2dojbr2q8iyx4" />
            </TenantAdminRoute>} data-id="element-232" data-mp="28rjdhdzmn0gk" />
        <Route path="/admin/:slug/menu-qr" element={<TenantAdminRoute data-id="element-236" data-mp="2mq9a6sijay4e">
              <MenuQRPage data-id="element-237" data-mp="2mq0yh3y5dyus" />
            </TenantAdminRoute>} data-id="element-235" data-mp="2io5vlv0kjdew" />
        <Route path="/admin/:slug/loyalty" element={<TenantAdminRoute data-id="element-239" data-mp="2rmzrfj62rzre">
              <LoyaltyDashboardPage data-id="element-240" data-mp="scrjiwmu1l55" />
            </TenantAdminRoute>} data-id="element-238" data-mp="2rp3r1owxyclv" />
        <Route path="/admin/:slug/loyalty/redemption" element={<TenantAdminRoute data-id="element-242" data-mp="20m8k966g7gy0">
              <LoyaltyRedemptionPage data-id="element-243" data-mp="20lgpfxkodn8s" />
            </TenantAdminRoute>} data-id="element-241" data-mp="xb2u59kybzof" />
        <Route path="/admin/:slug/invoice-requests" element={<TenantAdminRoute data-id="element-245" data-mp="29mwvxu8dd57q">
              <InvoiceRequestsPage data-id="element-246" data-mp="29nzoe6lcbfpr" />
            </TenantAdminRoute>} data-id="element-244" data-mp="1vnowr1c7hx3j" />
        {/* Emilius Pizza — Pedidos Web y Servicio Express. Ambas
            pantallas se auto-restringen al restaurante 27; para
            cualquier otro muestran "Módulo no disponible". */}
        <Route path="/admin/:slug/pedidos-web" element={<TenantAdminRoute data-id="element-248" data-mp="2sdenhvhgturi">
              <WebOrdersPage data-id="element-249" data-mp="2sdo7n2ftx7pa" />
            </TenantAdminRoute>} data-id="element-247" data-mp="2emje8i321bne" />
        <Route path="/admin/:slug/servicio-express" element={<TenantAdminRoute data-id="element-251" data-mp="32blsrh2e6odo">
              <ServicioExpressPage data-id="element-252" data-mp="32asqasagul1c" />
            </TenantAdminRoute>} data-id="element-250" data-mp="2oc2stbjqijcg" />

        {/* Driver App Routes */}
        <Route path="/driver/login" element={<DriverLoginPage data-id="element-254" data-mp="2xaobd6p7hyd8" />} data-id="element-253" data-mp="2xe2eih5p2blq" />
        <Route path="/driver/app" element={<DriverAppPage data-id="element-256" data-mp="3gyu6opn357tc" />} data-id="element-255" data-mp="3gxrfshprj5df" />

        {/* Catch-all */}
        <Route path="/factura/:paymentId" element={<InvoicePage data-id="element-258" data-mp="3lwvs3h7zhj7z" />} data-id="element-257" data-mp="3lxxeghug9s50" />
        <Route path="/ticket/:orderNumber" element={<TicketPage data-id="element-260" data-mp="370wke7osdl3d" />} data-id="element-259" data-mp="3lv1havqff7hm" />
        <Route path="*" element={<Navigate to="/" replace data-id="element-262" data-mp="3727rohhfxaz8" />} data-id="element-261" data-mp="36zlhea360hdb" />
      </Routes>
    </Suspense>;
}
export function App() {
  // ─── Disable browser auto-translate for the whole app ───
  // Some mobile browsers (Chrome, Safari) auto-translate menu items like
  // "Frozen Capuccino" → "Capuchino helado". We set the document language
  // to Spanish and inject the Google notranslate meta so the menu names
  // are always shown exactly as stored. The translate="no" on the root
  // div below is inherited by every descendant (products, categories,
  // prices, modals, cart, checkout).
  useEffect(() => {
    try {
      document.documentElement.lang = 'es';
      const metaName = 'google';
      let meta = document.querySelector(`meta[name="${metaName}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = metaName;
        document.head.appendChild(meta);
      }
      meta.content = 'notranslate';
    } catch {
      /* silent */
    }
  }, []);
  return <LanguageProvider data-id="element-263" data-mp="2kssusr4iqvyl">
      <CartProvider data-id="element-264" data-mp="2ktaphjbj924e">
        <TableOrderProvider data-id="element-265" data-mp="2krzilamsje92">
          <KitchenPauseProvider data-id="element-266" data-mp="2kuvi33eaeazm">
            <BrowserRouter data-id="element-267" data-mp="2gqxk4w0p7685">
              <ScrollToTop data-id="element-268" data-mp="2gs8ujeonfc88" />
              <KitchenPausedBanner data-id="element-269" data-mp="2gsidiiba0j4f" />
              <div translate="no" className="notranslate min-h-screen w-full flex flex-col bg-brand-offwhite font-sans text-gray-900" data-id="element-270" data-mp="2c0qtyrqjfdns">
                <AppRoutes data-id="element-271" data-mp="26z1m1kym3g3x" />
              </div>
            </BrowserRouter>
          </KitchenPauseProvider>
        </TableOrderProvider>
      </CartProvider>
    </LanguageProvider>;
}